import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { Property } from "@/types/property";
import { Edit, Trash, ScrollText, Wifi, KeyRound } from "lucide-react";
import { useState } from "react";
import DeleteDialog from "../shared/DeleteDialog";
import { usePropertyMutations } from "@/hooks/usePropertyMutations";
import { formatISOToDDMMYY } from "@/utils/dateFormatter";

interface PropertyRowProps {
  property: Property;
  onEdit: (property: Property) => void;
  onOpenRules: (property: Property) => void;
  onOpenEntry: (property: Property) => void;
  onOpenWifi: (property: Property) => void;
  canDelete: boolean;
}

const PropertyRow = ({ property, onEdit, onOpenRules, onOpenEntry, onOpenWifi, canDelete }: PropertyRowProps) => {
  const { deleteProperty } = usePropertyMutations();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteProperty.mutateAsync(property.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatCharacteristics = () => {
    return `${property.guests} ${property.guests === 1 ? 'persona' : 'personas'} | ${property.bedrooms} hab | ${property.bathrooms} ${property.bathrooms === 1 ? 'baño' : 'baños'}`;
  };

  return (
    <TableRow>
      <TableCell>
        <div className="space-y-0.5">
          <div className="font-medium">{property.shortName || 'N/A'}</div>
          <div className="text-sm text-muted-foreground">{property.name || '-'}</div>
        </div>
      </TableCell>
      
      <TableCell>
        {property.operationStartedAt ? formatISOToDDMMYY(property.operationStartedAt) : 'N/A'}
      </TableCell>
      
      <TableCell className="max-w-xs">
        <div className="line-clamp-2 leading-tight">
          {property.title}
        </div>
      </TableCell>
      
      <TableCell>
        <Badge variant={property.available ? "default" : "secondary"}>
          {property.available ? "Disponible" : "No disponible"}
        </Badge>
      </TableCell>
      
      <TableCell>
        <Badge variant={property.featured ? "default" : "outline"}>
          {property.featured ? "Sí" : "No"}
        </Badge>
      </TableCell>
      
      <TableCell>
        <div className="space-y-0.5">
          <div className="font-medium">{formatPrice(property.price)}</div>
          <div className="text-sm text-muted-foreground">{formatCharacteristics()}</div>
        </div>
      </TableCell>
      
      <TableCell className="max-w-xs">
        <div className="truncate">
          {property.location}
        </div>
      </TableCell>
      
      <TableCell>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenRules(property)}
            title="Reglas de la casa"
          >
            <ScrollText className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenEntry(property)}
            title="Instrucciones de entrada"
          >
            <KeyRound className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenWifi(property)}
            title="Configuración WiFi"
          >
            <Wifi className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(property)}
            title="Editar propiedad"
          >
            <Edit className="h-4 w-4" />
          </Button>
          {canDelete && (
            <DeleteDialog
              title="Eliminar propiedad"
              description={`¿Estás seguro de que deseas eliminar la propiedad "${property.title}"? Esta acción no se puede deshacer.`}
              onConfirm={handleDelete}
            >
              <Button
                variant="destructive"
                size="sm"
                disabled={isDeleting}
                title="Eliminar"
              >
                <Trash className="h-4 w-4" />
              </Button>
            </DeleteDialog>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default PropertyRow;